<template>
  <div class="linehaul-calculator">
    <!-- Page Header -->
    <div class="page-header">
      <v-container fluid class="px-6">
        <div class="d-flex align-center justify-space-between py-4">
          <div class="d-flex align-center">
            <div class="header-icon-badge mr-4">
              <v-icon size="20" class="header-icon-badge__icon">fas fa-route</v-icon>
            </div>
            <div>
              <div class="d-flex align-center">
                <h1 class="text-h5 font-weight-bold mb-0" style="color: #1a202c;">Line-haul Calculator</h1>
                <span class="header-tag ml-3">LOGISTICS</span>
              </div>
              <p class="body-2 mb-0 mt-1" style="color: #718096;">Route optimisation for production & consolidation depots</p>
            </div>
          </div>
          <div class="d-none d-md-flex align-center">
            <span class="header-date-chip">
              <v-icon size="12" class="mr-1">fas fa-clock</v-icon>
              {{ new Date().toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) }}
            </span>
          </div>
        </div>
      </v-container>
    </div>

    <v-container fluid class="px-6 pt-6 pb-10 page-body">
      <!-- Progress Stepper -->
      <div class="stepper-bar mb-6" role="navigation" aria-label="Calculator progress">
        <template v-for="(step, index) in flowSteps">
          <div :key="'dot-' + index" class="stepper-item" :class="{ 'stepper-item--done': index < stepIndex, 'stepper-item--active': index === stepIndex }">
            <div class="stepper-dot">
              <v-icon v-if="index < stepIndex" size="12">fas fa-check</v-icon>
              <span v-else>{{ index + 1 }}</span>
            </div>
            <span class="stepper-label">{{ step }}</span>
          </div>
          <div v-if="index < flowSteps.length - 1" :key="'line-' + index" class="stepper-line" :class="{ 'stepper-line--filled': index < stepIndex }"></div>
        </template>
      </div>

      <!-- Step 1: Parameters Card -->
      <v-card ref="parametersCard" class="mb-6 form-card" rounded="lg">
        <div class="form-card-accent"></div>
        <v-card-text class="pa-5 pb-4">
          <div class="d-flex align-center justify-space-between mb-4">
            <div class="d-flex align-center">
              <span class="step-chip mr-3">Step 1</span>
              <v-icon color="blue-grey darken-1" size="18" class="mr-2">fas fa-sliders-h</v-icon>
              <span class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 text-uppercase" style="letter-spacing: 0.8px;">Parameters</span>
            </div>
            <v-divider class="mx-4"></v-divider>
          </div>
          <v-row align="center" class="enterprise-inputs">
            <v-col cols="12" md="4" v-if="isInternalUser">
              <label class="field-label">Client *</label>
              <v-autocomplete
                v-model="selectedClientId"
                :items="clients"
                item-text="name"
                item-value="id"
                placeholder="Select client"
                prepend-inner-icon="fas fa-building"
                outlined
                dense
                clearable
                hide-no-data
                hide-details="auto"
                :loading="clientsLoading"
                :rules="[clientRequiredRule]"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" :md="isInternalUser ? 4 : 6">
              <label class="field-label">Delivery Suburb</label>
              <v-autocomplete
                v-model="deliverySuburb"
                :items="suburbSuggestions"
                :search-input.sync="suburbSearch"
                :loading="suburbLoading"
                item-text="display"
                item-value="display"
                placeholder="Suburb or postcode"
                prepend-inner-icon="fas fa-search-location"
                clearable
                outlined
                dense
                hide-no-data
                hide-details="auto"
                @change="onSuburbSelect"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" :md="isInternalUser ? 4 : 6">
              <label class="field-label">Delivery Date</label>
              <DatePicker
                ref="deliveryDatePicker"
                :date_props="deliveryDate"
                @interface="onDeliveryDateChange"
                :allowedDates="allowedDeliveryDates"
                outlined
                dense
                clearable
                :hideDetails="'auto'"
              />
            </v-col>
          </v-row>

          <v-row class="mt-2 mb-0">
            <v-col cols="12" class="d-flex justify-end">
              <v-btn
                color="primary"
                :disabled="!canGoNext"
                :loading="cutoffsLoading"
                @click="goToCutoffs"
                outlined
                class="text-none font-weight-bold search-btn px-6"
                height="38"
              >
                Next
                <v-icon right small>fas fa-arrow-right</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Cutoffs loading -->
      <v-card v-if="cutoffsLoading" class="mb-6 form-card" rounded="lg" flat>
        <v-card-text class="pa-6 text-center">
          <v-progress-circular indeterminate color="primary" size="42" width="4" class="mb-3"></v-progress-circular>
          <div class="body-2 grey--text text--darken-1">Loading cutoffs for the selected delivery date…</div>
        </v-card-text>
      </v-card>

      <!-- Step 2 + 3: Cutoffs and Fine Tune -->
      <div v-if="cutoffs" ref="cutoffsArea">
        <v-card class="mb-6 form-card" rounded="lg">
          <div class="form-card-accent"></div>
          <v-card-text class="pa-5 pb-4">
            <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
              <div class="d-flex align-center">
                <span class="step-chip mr-3">Step 2</span>
                <v-icon color="blue-grey darken-1" size="18" class="mr-2">fas fa-hourglass-half</v-icon>
                <span class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 text-uppercase" style="letter-spacing: 0.8px;">Cutoffs</span>
              </div>
              <div class="d-flex align-center flex-wrap cutoffs-meta">
                <v-chip v-if="cutoffs.client_name" small outlined color="blue-grey" class="mr-2 font-weight-bold">{{ cutoffs.client_name }}</v-chip>
                <v-chip small outlined color="blue-grey" class="mr-2 font-weight-bold">{{ cutoffs.suburb }} {{ cutoffs.postcode }}</v-chip>
                <v-chip small outlined color="blue-grey" class="font-weight-bold">{{ cutoffs.delivery_date }}</v-chip>
              </div>
            </div>

            <v-row>
              <v-col cols="12" sm="6" md="4">
                <div class="stat-card stat-card--blue" title="Last feasible consolidation date for this delivery date">
                  <div class="stat-card__icon">
                    <v-icon color="white" size="16">fas fa-boxes</v-icon>
                  </div>
                  <div class="stat-card__content">
                    <div class="stat-card__label">Latest Consolidation</div>
                    <div class="stat-card__value">{{ formatDateTime(cutoffs.latest_consolidation) || 'Not available' }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="stat-card stat-card--indigo" title="Latest date/time an order can be created for this delivery date">
                  <div class="stat-card__icon">
                    <v-icon color="white" size="16">fas fa-calendar-check</v-icon>
                  </div>
                  <div class="stat-card__content">
                    <div class="stat-card__label">Submission Cutoff</div>
                    <div class="stat-card__value">{{ formatDateTime(cutoffs.submission_cutoff) || '-' }}</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="stat-card stat-card--teal" title="Latest date/time the delivery can be modified">
                  <div class="stat-card__icon">
                    <v-icon color="white" size="16">fas fa-edit</v-icon>
                  </div>
                  <div class="stat-card__content">
                    <div class="stat-card__label">Modify Cutoff</div>
                    <div class="stat-card__value">{{ formatDateTime(cutoffs.modify_cutoff) || '-' }}</div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-alert
              v-if="cutoffs.note"
              dense
              outlined
              type="warning"
              class="mb-0 mt-4 note-alert"
            >
              <span class="note-text">{{ cutoffs.note }}</span>
              <span v-if="cutoffs.cutoff_timezone" class="note-timezone"> ({{ cutoffs.cutoff_timezone }})</span>
            </v-alert>

            <v-alert
              v-if="!isPickPack && !cutoffs.latest_consolidation && !cutoffs.local"
              dense
              outlined
              type="error"
              class="mb-0 mt-4"
            >
              <span class="alert-text">No linehaul is available for the selected delivery date.</span>
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Step 3: Fine Tune -->
        <v-card class="mb-6 form-card" rounded="lg">
          <div class="form-card-accent"></div>
          <v-card-text class="pa-5 pb-4">
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <span class="step-chip mr-3">Step 3</span>
                <v-icon color="blue-grey darken-1" size="18" class="mr-2">fas fa-sliders-h</v-icon>
                <span class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 text-uppercase" style="letter-spacing: 0.8px;">Fine Tune</span>
              </div>
              <v-divider class="mx-4"></v-divider>
            </div>

            <!-- Local short-circuit: recommended depot delivers directly, no linehaul needed -->
            <v-alert
              v-if="cutoffsLocal"
              dense
              outlined
              type="success"
              class="mb-0 mt-2"
            >
              <span class="alert-text">{{ cutoffs.message || 'Good news, we can deliver to that suburb directly from the production depot. No linehaul is required.' }}</span>
            </v-alert>

            <div v-if="cutoffsLocal" class="d-flex justify-end mt-2">
              <v-btn
                text
                small
                color="primary"
                class="text-none font-weight-bold"
                @click="showAdvancedOptions = !showAdvancedOptions"
              >
                <v-icon left small>{{ showAdvancedOptions ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                Advanced: see linehaul options
              </v-btn>
            </div>

            <template v-if="!cutoffsLocal || showAdvancedOptions">
              <v-row align="center" class="enterprise-inputs mt-2">
                <v-col cols="12" md="6">
                  <label class="field-label">Earliest Consolidation</label>
                  <v-alert
                    v-if="isPickPack && productionsKnownEmpty"
                    dense
                    outlined
                    type="warning"
                    class="mb-0"
                  >
                    <span class="alert-text">No production is available for the selected delivery date - try a later delivery date.</span>
                  </v-alert>
                  <v-select
                    v-else-if="isPickPack"
                    v-model="earliestConsolidation"
                    :items="productionDateOptions"
                    item-text="text"
                    item-value="value"
                    placeholder="System selects production"
                    prepend-inner-icon="fas fa-calendar-check"
                    clearable
                    outlined
                    dense
                    hide-details="auto"
                  ></v-select>
                  <template v-else>
                    <DatePicker
                      v-if="earliestConsolidationDates.length"
                      ref="earliestConsolidationPicker"
                      :date_props="earliestConsolidation"
                      @interface="onEarliestConsolidationChange"
                      :allowedDates="allowedEarliestConsolidationDates"
                      outlined
                      dense
                      clearable
                      :hideDetails="'auto'"
                    />
                    <v-alert
                      v-else
                      dense
                      outlined
                      type="warning"
                      class="mb-0"
                    >
                      <span class="alert-text">No linehaul is available for the selected delivery date.</span>
                    </v-alert>
                  </template>
                </v-col>

                <v-col cols="12" md="6" v-if="!isPickPack">
                  <label class="field-label">Consolidation Depot</label>
                  <v-select
                    v-model="selectedConsolidationDepot"
                    :items="consolidationDepotItems"
                    item-text="name"
                    item-value="depot_id"
                    placeholder="Recommended depot (or select one)"
                    prepend-inner-icon="fas fa-warehouse"
                    clearable
                    outlined
                    dense
                    hide-details="auto"
                  ></v-select>
                  <v-alert
                    v-if="!consolidationDepotItems.length"
                    dense
                    outlined
                    type="warning"
                    class="mb-0 mt-2"
                  >
                    <span class="alert-text">No consolidation depots were returned for this client, suburb and delivery date.</span>
                  </v-alert>
                </v-col>
              </v-row>

              <v-alert
                v-if="isLocalProductionSelected && !cutoffsLocal"
                dense
                outlined
                type="success"
                class="mb-0 mt-2"
              >
                <span class="alert-text">Good news — this production depot can deliver to that suburb directly. No linehaul is required.</span>
              </v-alert>

              <v-alert
                v-if="isPickPack && (!cutoffs.productions || !cutoffs.productions.length)"
                dense
                outlined
                type="error"
                class="mb-0 mt-2"
              >
                <span class="alert-text">No productions available for this client before the delivery date.</span>
              </v-alert>
            </template>

            <v-row class="mt-2 mb-0">
              <v-col cols="12" class="d-flex justify-end">
                <v-btn
                  text
                  class="text-none font-weight-bold mr-2"
                  height="38"
                  @click="backToParameters"
                >
                  <v-icon left small>fas fa-arrow-left</v-icon>
                  Back
                </v-btn>
                <v-btn
                  v-if="!cutoffsLocal || showAdvancedOptions"
                  color="primary"
                  :disabled="!canCalculate"
                  :loading="routesLoading"
                  @click="calculateRoutes"
                  outlined
                  class="text-none font-weight-bold search-btn px-6"
                  height="38"
                >
                  <v-icon left small>fas fa-search</v-icon>
                  Calculate
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </div>

      <!-- Step 4: Results -->
      <div v-if="hasSearched && routesResult" ref="resultsArea">
        <!-- Local delivery: green banner -->
        <v-card v-if="routesResult.local" class="mb-5 local-banner" rounded="lg" flat>
          <v-card-text class="pa-4">
            <div class="d-flex align-center flex-wrap">
              <div class="summary-badge mr-3">
                <v-icon color="white" size="16">fas fa-check</v-icon>
              </div>
              <div class="grow">
                <div class="body-1 font-weight-bold grey--text text--darken-4">No linehaul required</div>
                <div class="body-2 grey--text text--darken-1 mt-1">
                  {{ routesResult.message || 'Good news, we can deliver to that suburb directly from the production depot. No linehaul is required.' }}
                </div>
              </div>
              <v-btn
                text
                small
                color="primary"
                class="text-none font-weight-bold mt-2 mt-sm-0"
                @click="backToParameters"
              >
                <v-icon left small>fas fa-arrow-left</v-icon>
                Adjust parameters
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <template v-else>
          <!-- Production Panel (pick pack only) -->
          <v-card v-if="isPickPack && routesResult.production" class="mb-5 production-panel" rounded="lg" flat>
            <v-card-text class="pa-4">
              <div class="section-header mb-3">
                <v-icon color="blue-grey darken-1" size="16" class="mr-2">fas fa-industry</v-icon>
                <span class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 text-uppercase" style="letter-spacing: 0.8px;">Production</span>
              </div>
              <v-row no-gutters>
                <v-col cols="12" sm="6" class="pr-sm-3">
                  <div class="stat-card stat-card--green">
                    <div class="stat-card__icon">
                      <v-icon color="white" size="16">fas fa-calendar-check</v-icon>
                    </div>
                    <div class="stat-card__content">
                      <div class="stat-card__label">Production Date</div>
                      <div class="stat-card__value">{{ routesResult.production.production_date || '-' }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" class="pt-3 pt-sm-0 pl-sm-3">
                  <div class="stat-card stat-card--indigo">
                    <div class="stat-card__icon">
                      <v-icon color="white" size="16">fas fa-industry</v-icon>
                    </div>
                    <div class="stat-card__content">
                      <div class="stat-card__label">Production Depot</div>
                      <div class="stat-card__value">{{ productionDepotNames || '-' }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Results Summary Banner -->
          <v-card v-if="routeOptions.length" class="mb-5 summary-banner" rounded="lg" flat>
            <v-card-text class="pa-4">
              <v-row align="center" no-gutters>
                <v-col cols="12" md="8">
                  <div class="d-flex align-center">
                    <div class="summary-badge mr-3">
                      <v-icon color="white" size="16">fas fa-check</v-icon>
                    </div>
                    <div>
                      <div class="body-2 font-weight-bold grey--text text--darken-4">
                        {{ routeOptions.length }} route{{ routeOptions.length !== 1 ? 's' : '' }} available
                      </div>
                      <div class="d-flex align-center mt-1 flex-wrap">
                        <span class="body-2 font-weight-medium grey--text text--darken-2 mr-2">{{ resultOriginDepotName }}</span>
                        <v-icon x-small color="blue-grey lighten-1" class="mx-1">fas fa-arrow-right</v-icon>
                        <span class="body-2 font-weight-medium grey--text text--darken-2 ml-2">{{ resultDestinationDepotName }}</span>
                      </div>
                      <div class="caption grey--text mt-1">
                        {{ searchContextLine }}
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="text-md-right mt-3 mt-md-0">
                  <span class="body-2 grey--text">Select a row to expand</span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Results Table -->
          <v-card v-if="routeOptions.length" class="mb-5 table-card" rounded="lg" flat>
            <v-data-table
              :headers="tableHeaders"
              :items="routeOptions"
              :items-per-page="10"
              class="routes-table"
              :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            >
              <template v-slot:item="{ item }">
                <tr
                  @click="toggleDetails(item)"
                  :class="{ 'selected-row': selectedOption === item }"
                  class="table-row"
                >
                  <td class="text-no-wrap">{{ formatDateTime(item._departure_date || item.departure) || '-' }}</td>
                  <td class="text-no-wrap">{{ formatDateTime(item._arrival_date || item.arrival) || '-' }}</td>
                  <td class="text-no-wrap">{{ item.delivery_ready_date || '-' }}</td>
                  <td>
                    <span class="legs-badge">{{ item.total_legs != null ? item.total_legs : (item.depots ? item.depots.length - 1 : '-') }}</span>
                  </td>
                  <td class="text-no-wrap">
                    <span class="font-weight-bold" :class="item.total_lifecycle_hours != null ? 'teal--text text--darken-2' : 'grey--text'">
                      {{ item.total_lifecycle_hours != null ? item.total_lifecycle_hours + ' hrs' : '--' }}
                    </span>
                  </td>
                  <td>
                    <v-icon size="18" color="blue-grey lighten-1" class="expand-icon">{{ selectedOption === item ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                  </td>
                </tr>
              </template>
            </v-data-table>
            <div class="pa-3 body-2 grey--text text--darken-1 table-footnote">
              Total Lifecycle Hours covers the full fulfilment window (creation cutoff → requested delivery date). Expand a row for linehaul transit hours, dwell and the route map.
            </div>
          </v-card>

          <!-- Detail Panel -->
          <v-expand-transition>
            <v-card v-if="selectedOption" class="mb-5 detail-card" rounded="lg" flat>
              <div class="detail-card-header pa-4">
                <div class="d-flex align-center">
                  <v-icon color="primary" size="16" class="mr-2">fas fa-info-circle</v-icon>
                  <span class="subtitle-1 font-weight-bold" style="color: #1a202c;">Route Details</span>
                  <v-spacer></v-spacer>
                  <v-btn icon small @click="selectedOption = null">
                    <v-icon size="18" color="grey darken-1">mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>

              <v-card-text class="pa-5">
                <!-- Stat Cards Row -->
                <v-row class="mb-1">
                  <v-col cols="12" sm="6" md="4">
                    <div class="stat-card stat-card--indigo">
                      <div class="stat-card__icon">
                        <v-icon color="white" size="16">fas fa-calendar-check</v-icon>
                      </div>
                      <div class="stat-card__content">
                        <div class="stat-card__label">Submission Cutoff</div>
                        <div class="stat-card__value">{{ formatDateTime(selectedOption.creation_cutoff) || '-' }}</div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="stat-card stat-card--blue">
                      <div class="stat-card__icon">
                        <v-icon color="white" size="16">fas fa-boxes</v-icon>
                      </div>
                      <div class="stat-card__content">
                        <div class="stat-card__label">Latest Consolidation</div>
                        <div class="stat-card__value">{{ formatDateTime(selectedOption.consolidation_cutoff) || '-' }}</div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <div class="stat-card stat-card--teal">
                      <div class="stat-card__icon">
                        <v-icon color="white" size="16">fas fa-truck-loading</v-icon>
                      </div>
                      <div class="stat-card__content">
                        <div class="stat-card__label">Delivery Ready</div>
                        <div class="stat-card__value">{{ selectedOption.delivery_ready_date || '-' }}</div>
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <!-- Route Metrics -->
                <v-row class="mb-1">
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-card stat-card--plain">
                      <div class="stat-card__icon">
                        <v-icon color="white" size="16">fas fa-warehouse</v-icon>
                      </div>
                      <div class="stat-card__content">
                        <div class="stat-card__label">Total Depots</div>
                        <div class="stat-card__value">{{ selectedOption.total_depots != null ? selectedOption.total_depots : (selectedOptionDepots.length || '-') }}</div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-card stat-card--plain">
                      <div class="stat-card__icon">
                        <v-icon color="white" size="16">fas fa-route</v-icon>
                      </div>
                      <div class="stat-card__content">
                        <div class="stat-card__label">Total Legs</div>
                        <div class="stat-card__value">{{ selectedOption.total_legs != null ? selectedOption.total_legs : (selectedOptionDepots.length ? selectedOptionDepots.length - 1 : '-') }}</div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-card stat-card--plain" title="First departure to final arrival — linehaul transit only">
                      <div class="stat-card__icon">
                        <v-icon color="white" size="16">fas fa-truck</v-icon>
                      </div>
                      <div class="stat-card__content">
                        <div class="stat-card__label">Total Linehaul Hours</div>
                        <div class="stat-card__value">{{ selectedOption.total_hours != null ? selectedOption.total_hours + ' hrs' : '-' }}</div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-card stat-card--plain" title="Total linehaul hours minus the sum of leg transit hours — time consignments wait at intermediate depots">
                      <div class="stat-card__icon">
                        <v-icon color="white" size="16">fas fa-hourglass-half</v-icon>
                      </div>
                      <div class="stat-card__content">
                        <div class="stat-card__label">Dwell Hours</div>
                        <div class="stat-card__value">{{ selectedRouteDwellHours != null ? selectedRouteDwellHours + ' hrs' : '-' }}</div>
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-divider class="my-5"></v-divider>

                <!-- Line-haul Map -->
                <div class="section-header mb-3">
                  <v-icon color="blue-grey darken-1" size="16" class="mr-2">fas fa-map-marked-alt</v-icon>
                  <span class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 text-uppercase" style="letter-spacing: 0.8px;">Route Map</span>
                </div>
                <v-card outlined rounded="lg" class="mb-6 map-container overflow-hidden">
                  <div style="height: 95vh; position: relative;">
                    <div v-if="mapLoading" class="map-loading-overlay">
                      <v-progress-circular indeterminate color="primary" size="56" width="5"></v-progress-circular>
                      <div class="mt-3 body-1 grey--text text--darken-1">Loading Line-haul map...</div>
                    </div>
                    <v-btn
                      v-if="initialBounds"
                      fab
                      small
                      color="white"
                      class="reset-map-btn"
                      @click="resetMap"
                      title="Reset map view"
                    >
                      <v-icon>mdi-crosshairs-gps</v-icon>
                    </v-btn>
                    <l-map
                      ref="routeMap"
                      :zoom="4"
                      :center="[-25.5, 134.5]"
                      :min-zoom="4"
                      :max-zoom="18"
                      :max-bounds="mapBounds"
                      :max-bounds-viscosity="1.0"
                      :options="{ zoomControl: true, attributionControl: true }"
                      style="height: 100%; width: 100%; z-index: 1;"
                      @ready="onRouteMapReady"
                    >
                      <l-tile-layer :url="tileUrl"></l-tile-layer>
                      <l-polyline
                        v-for="(leg, lIndex) in routeMapLegs"
                        :key="'leg-' + lIndex"
                        :lat-lngs="leg.path"
                        :color="leg.color"
                        :weight="3"
                        :opacity="0.8"
                      ></l-polyline>
                      <l-marker
                        v-for="(leg, lIndex) in routeMapLegs"
                        :key="'legnum-' + lIndex"
                        :lat-lng="leg.midpoint"
                        :icon="createLegIcon(lIndex + 1, leg.color)"
                      ></l-marker>
                      <l-marker
                        v-for="(leg, lIndex) in routeMapLegs"
                        :key="'arrow-' + lIndex"
                        :lat-lng="leg.arrowPoint"
                        :icon="createArrowIcon(leg.bearing, leg.color)"
                      ></l-marker>
                      <l-marker
                        v-for="(coords, mIndex) in routeMapMarkers"
                        :key="'marker-' + mIndex"
                        :lat-lng="coords.latLng"
                        :icon="createDepotIcon(mIndex, routeMapMarkers.length)"
                      >
                        <l-tooltip :options="{ permanent: true, direction: tooltipDirection(mIndex), offset: tooltipOffset(mIndex) }">
                          {{ coords.name }} {{ coords.dateLabel }}
                        </l-tooltip>
                      </l-marker>
                    </l-map>
                  </div>
                </v-card>

                <v-divider class="my-5"></v-divider>

                <!-- Line-haul Hops -->
                <div class="section-header mb-3">
                  <v-icon color="blue-grey darken-1" size="16" class="mr-2">fas fa-project-diagram</v-icon>
                  <span class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 text-uppercase" style="letter-spacing: 0.8px;">Route Legs</span>
                  <v-chip small color="blue-grey lighten-4" class="ml-3 font-weight-bold blue-grey--text text--darken-2">
                    {{ selectedOptionDepots.length }} stops
                  </v-chip>
                  <v-chip small outlined color="blue-grey" class="ml-2 font-weight-bold">
                    {{ selectedOptionDepots.length ? selectedOptionDepots.length - 1 : '-' }} legs
                  </v-chip>
                </div>
                <v-timeline dense align-top v-if="selectedOptionDepots.length">
                  <v-timeline-item
                    v-for="(hop, hIndex) in selectedOptionDepots"
                    :key="hIndex"
                    color="transparent"
                    large
                  >
                    <template v-slot:icon>
                      <div
                        class="outline-dot-circle"
                        :style="{ borderColor: hopColorHex(hIndex), color: hopColorHex(hIndex) }"
                      >{{ hIndex + 1 }}</div>
                    </template>
                    <v-card class="hop-card" outlined rounded="lg">
                      <v-card-text class="pa-4">
                        <div class="d-flex align-center justify-space-between flex-wrap">
                          <div class="d-flex align-center">
                            <div>
                              <div class="d-flex align-center mb-1">
                                <span class="subtitle-1 font-weight-bold grey--text text--darken-4">
                                  {{ hop.name || getDepotName(hop.depot_id) || ('Depot ' + hop.depot_id) }}
                                </span>
                                <v-chip
                                  x-small
                                  :color="hopColor(hIndex)"
                                  dark
                                  class="ml-2 font-weight-bold"
                                >
                                  {{ hopLabel(hIndex) }}
                                </v-chip>
                              </div>
                            </div>
                          </div>
                          <div class="d-flex align-center mt-2 mt-sm-0">
                            <div v-if="hop.arrival_date" class="hop-date-badge mr-3">
                              <v-icon x-small color="green darken-1" class="mr-1">fas fa-truck-loading</v-icon>
                              <span class="hop-date-text green--text text--darken-1">Arr {{ hop.arrival_date }}</span>
                            </div>
                            <div v-if="hop.departure_date" class="hop-date-badge mr-3">
                              <v-icon x-small color="blue darken-1" class="mr-1">fas fa-truck</v-icon>
                              <span class="hop-date-text blue--text text--darken-1">Dep {{ hop.departure_date }}</span>
                            </div>
                            <div v-if="hop.hours != null" class="hop-date-badge" title="Transit hours for the leg departing this depot">
                              <v-icon x-small color="teal darken-1" class="mr-1">fas fa-clock</v-icon>
                              <span class="hop-date-text teal--text text--darken-1">{{ hop.hours }} hrs</span>
                            </div>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
              </v-card-text>
            </v-card>
          </v-expand-transition>

          <!-- No results message -->
          <v-card v-if="!routeOptions.length" class="mb-4" rounded="lg">
            <v-card-text class="pa-6">
              <v-alert type="warning" prominent outlined class="mb-0">
                <div class="body-1">No line-haul routes found for the selected criteria.</div>
                <div class="d-flex align-center mt-3">
                  <v-btn
                    text
                    small
                    color="primary"
                    class="text-none font-weight-bold"
                    @click="backToParameters"
                  >
                    <v-icon left small>fas fa-arrow-left</v-icon>
                    Adjust parameters
                  </v-btn>
                </div>
              </v-alert>
            </v-card-text>
          </v-card>
        </template>
      </div>

      <!-- Routes loading -->
      <v-card v-if="routesLoading" class="mb-5 table-card" rounded="lg" flat>
        <v-card-text class="pa-6 text-center">
          <v-progress-circular indeterminate color="primary" size="42" width="4" class="mb-3"></v-progress-circular>
          <div class="body-2 grey--text text--darken-1">Calculating line-haul routes…</div>
        </v-card-text>
      </v-card>

      <v-dialog v-model="errorDialog" max-width="520">
        <v-card>
          <v-card-title class="headline d-flex align-center">
            <v-icon color="red darken-1" class="mr-3">fas fa-exclamation-triangle</v-icon>
            <span>Something went wrong</span>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="body-1 pt-4">{{ errorMessage }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="errorDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';
import L from 'leaflet';
import { LMap, LTileLayer, LMarker, LTooltip, LPolyline } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import DatePicker from '../../parts/DatePicker.vue';

export default {
  name: "LinehaulCalculator",
  components: {
    DatePicker,
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LPolyline,
  },
  data() {
    return {
      // Step 1: parameters
      selectedClientId: null,
      deliverySuburb: null,
      selectedSuburbData: null,
      suburbSearch: null,
      deliveryDate: null,

      // Client data
      clients: [],
      clientsLoading: false,

      // Loading states
      suburbLoading: false,
      cutoffsLoading: false,
      routesLoading: false,

      // Depot data (fallback for names/coords)
      allDepots: [],

      // Suburb autocomplete
      suburbSuggestions: [],

      // Step 2: cutoffs
      cutoffs: null,

      // Step 3: fine tune
      earliestConsolidation: null,
      selectedConsolidationDepot: null,
      showAdvancedOptions: false,

      // Step 4: results
      hasSearched: false,
      routesResult: null,
      routeOptions: [],
      errorDialog: false,
      errorMessage: '',

      // Detail panel
      selectedOption: null,
      depotCoordsCache: {},
      routeMapMarkers: [],
      routeMapLegs: [],
      mapLoading: false,
      initialBounds: null,
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',

      // Table headers
      tableHeaders: [
        { text: 'Departure', value: 'departure', sortable: true },
        { text: 'Arrival', value: 'arrival', sortable: true },
        { text: 'Delivery Ready', value: 'delivery_ready_date', sortable: true },
        { text: 'Total Legs', value: 'total_legs', sortable: true },
        { text: 'Total Lifecycle Hours', value: 'total_lifecycle_hours', sortable: true },
        { text: '', value: 'actions', sortable: false, width: '48px' },
      ],
    };
  },
  computed: {
    isInternalUser() {
      return this.$store.getters['auth/isInternalUser'];
    },
    isPickPack() {
      return !!(this.cutoffs && this.cutoffs.is_pick_pack);
    },
    cutoffsLocal() {
      return !!(this.cutoffs && this.cutoffs.local);
    },
    flowSteps() {
      return ['Parameters', 'Cutoffs', 'Fine Tune', 'Results'];
    },
    stepIndex() {
      if (this.hasSearched && this.routesResult) return 3;
      if (this.cutoffs) return 2;
      return 0;
    },
    searchClientName() {
      if (!this.cutoffs) return null;
      if (this.cutoffs.client_name) return this.cutoffs.client_name;
      if (this.isInternalUser && this.selectedClientId != null) {
        const client = this.clients.find(c => c.id === this.selectedClientId);
        return client ? client.name : null;
      }
      return null;
    },
    searchContextLine() {
      const parts = [];
      if (this.searchClientName) parts.push(this.searchClientName);
      if (this.selectedSuburbData) {
        parts.push([this.selectedSuburbData.suburb_name, this.selectedSuburbData.postcode].filter(Boolean).join(' '));
      }
      if (this.deliveryDate) parts.push('Delivery ' + this.deliveryDate);
      return parts.join('  ·  ');
    },
    canGoNext() {
      if (this.isInternalUser && !this.selectedClientId) return false;
      return !!(this.deliveryDate && this.selectedSuburbData);
    },
    canCalculate() {
      if (!this.cutoffs) return false;
      if (this.isPickPack) {
        return !!(this.cutoffs.productions && this.cutoffs.productions.length);
      }
      // Empty earliest_consolidation_dates = no feasible linehaul for this delivery date
      return this.earliestConsolidationDates.length > 0;
    },
    productionDateOptions() {
      if (!this.cutoffs || !Array.isArray(this.cutoffs.productions)) return [];
      return this.cutoffs.productions.map(p => {
        let text = p.date;
        if (p.production_depot) text += ' — ' + p.production_depot;
        if (p.local) text += ' (direct to suburb)';
        return { text, value: p.date };
      });
    },
    productionsKnownEmpty() {
      // True only when the API explicitly reported an empty productions list (no production can
      // make the selected delivery date). An absent list means an older API without the field -
      // keep the plain selector in that case rather than claiming there is no production.
      return !!(this.cutoffs && this.isPickPack && Array.isArray(this.cutoffs.productions) && !this.cutoffs.productions.length);
    },
    selectedProduction() {
      if (!this.isPickPack || !this.cutoffs || !Array.isArray(this.cutoffs.productions) || !this.earliestConsolidation) return null;
      return this.cutoffs.productions.find(p => p.date === this.earliestConsolidation) || null;
    },
    isLocalProductionSelected() {
      return !!(this.isPickPack && this.selectedProduction && this.selectedProduction.local);
    },
    consolidationDepotItems() {
      if (!this.cutoffs) return [];
      const raw = Array.isArray(this.cutoffs.consolidation_depots)
        ? this.cutoffs.consolidation_depots
        : [];
      return raw
        .map(d => {
          const id = d.depot_id !== undefined && d.depot_id !== null ? d.depot_id : d.id;
          const name = d.name || d.depot_name || (id != null ? 'Depot ' + id : null);
          return {
            depot_id: id,
            // API sorts the local/direct-delivery depot first and flags it with local: 0|1
            name: name && Number(d.local) === 1 ? name + ' (direct)' : name,
          };
        })
        .filter(d => d.depot_id !== undefined && d.depot_id !== null && d.name);
    },
    earliestConsolidationDates() {
      if (!this.cutoffs || this.isPickPack) return [];
      return Array.isArray(this.cutoffs.earliest_consolidation_dates)
        ? this.cutoffs.earliest_consolidation_dates
        : [];
    },
    productionDepotNames() {
      if (!this.routesResult || !this.routesResult.production || !Array.isArray(this.routesResult.production.depots)) return '';
      return this.routesResult.production.depots.map(d => d.depot_name || d.name).filter(Boolean).join(', ');
    },
    selectedOptionDepots() {
      if (!this.selectedOption || !this.selectedOption.depots || !Array.isArray(this.selectedOption.depots)) return [];
      return this.selectedOption.depots;
    },
    selectedRouteDwellHours() {
      if (!this.selectedOption || this.selectedOption.total_hours == null || !this.selectedOptionDepots.length) return null;
      let sum = 0;
      let hasHours = false;
      this.selectedOptionDepots.forEach(d => {
        if (d.hours != null) {
          sum += Number(d.hours);
          hasHours = true;
        }
      });
      if (!hasHours) return null;
      const gap = Number(this.selectedOption.total_hours) - sum;
      return gap >= 0 ? gap : null;
    },
    mapBounds() {
      return L.latLngBounds([-10, 110], [-48, 179]);
    },
    resultOriginDepotName() {
      if (!this.routeOptions.length) return '';
      const depots = this.routeOptions[0].depots;
      if (!depots || !depots.length) return '';
      return this.getDepotName(depots[0].depot_id) || depots[0].name || ('Depot ' + depots[0].depot_id);
    },
    resultDestinationDepotName() {
      if (!this.routeOptions.length) return '';
      const depots = this.routeOptions[0].depots;
      if (!depots || !depots.length) return '';
      const last = depots[depots.length - 1];
      return this.getDepotName(last.depot_id) || last.name || ('Depot ' + last.depot_id);
    },
  },
  mounted() {
    this.fetchDepots();
    if (this.isInternalUser) {
      this.fetchClients();
    }
  },
  watch: {
    suburbSearch(val) {
      if (!val || val.length < 2) return;
      if (this.suburbLoading) return;
      if (this.deliverySuburb && this.deliverySuburb === val) return;
      this.searchSuburbs(val);
    },
    selectedOption() {
      this.$nextTick(() => this.updateRouteMap());
    },
    selectedClientId() {
      this.invalidateCutoffs();
    },
    selectedSuburbData() {
      this.invalidateCutoffs();
    },
    deliveryDate() {
      this.invalidateCutoffs();
    },
  },
  methods: {
    clientRequiredRule(value) {
      return !this.isInternalUser || !!value || 'Client is required';
    },
    invalidateCutoffs() {
      if (!this.cutoffs && !this.hasSearched) return;
      this.cutoffs = null;
      this.earliestConsolidation = null;
      this.selectedConsolidationDepot = null;
      this.showAdvancedOptions = false;
      this.hasSearched = false;
      this.routesResult = null;
      this.routeOptions = [];
      this.selectedOption = null;
    },
    backToParameters() {
      this.cutoffs = null;
      this.earliestConsolidation = null;
      this.selectedConsolidationDepot = null;
      this.showAdvancedOptions = false;
      this.hasSearched = false;
      this.routesResult = null;
      this.routeOptions = [];
      this.selectedOption = null;
      this.scrollToSection('parametersCard');
    },
    getDepartureDate(item) {
      if (!item || !item.depots || !item.depots.length) return null;
      return item.depots[0].departure_date || null;
    },
    getArrivalDate(item) {
      if (!item || !item.depots || !item.depots.length) return null;
      const lastHop = item.depots[item.depots.length - 1];
      return lastHop.arrival_date || null;
    },
    normalizeRouteDepots(route) {
      if (!route) return [];

      const routeDepots = Array.isArray(route.depots) ? route.depots.map(d => ({ ...d })) : [];
      const routePath = route.route_path || route.path || '';
      const orderedIds = [];

      if (typeof routePath === 'string') {
        const matchIds = routePath.match(/\d+/g) || [];
        matchIds.forEach(id => {
          const idValue = Number(id);
          if (!Number.isNaN(idValue) && !orderedIds.includes(idValue)) orderedIds.push(idValue);
        });
      } else if (Array.isArray(routePath)) {
        routePath.forEach(id => {
          const idValue = Number(id);
          if (!Number.isNaN(idValue) && !orderedIds.includes(idValue)) orderedIds.push(idValue);
        });
      }

      const identified = new Map();
      routeDepots.forEach(depot => {
        const depotId = Number(depot.depot_id !== undefined && depot.depot_id !== null ? depot.depot_id : depot.id);
        if (!Number.isNaN(depotId)) {
          identified.set(depotId, { ...depot, depot_id: depotId });
        }
      });

      const normalized = [];
      orderedIds.forEach(id => {
        const existing = identified.get(id);
        if (existing) {
          normalized.push(existing);
          identified.delete(id);
          return;
        }

        const fallback = this.allDepots.find(d => Number(d.id) === Number(id));
        if (fallback) {
          normalized.push({
            depot_id: Number(fallback.id),
            id: Number(fallback.id),
            name: fallback.name,
            latitude: fallback.latitude,
            longitude: fallback.longitude,
            arrival_date: null,
            departure_date: null,
          });
        }
      });

      identified.forEach(depot => normalized.push(depot));

      const uniqueById = [];
      const seen = new Set();
      normalized.forEach(depot => {
        const depotId = Number(depot.depot_id !== undefined && depot.depot_id !== null ? depot.depot_id : depot.id);
        if (Number.isNaN(depotId) || seen.has(depotId)) return;
        seen.add(depotId);
        const allDepot = this.allDepots.find(d => Number(d.id) === depotId);
        uniqueById.push({
          ...depot,
          depot_id: depotId,
          id: depot.id !== undefined && depot.id !== null ? depot.id : depotId,
          name: depot.name || (allDepot ? allDepot.name : null),
          latitude: depot.latitude !== undefined && depot.latitude !== null ? depot.latitude : (allDepot ? allDepot.latitude : null),
          longitude: depot.longitude !== undefined && depot.longitude !== null ? depot.longitude : (allDepot ? allDepot.longitude : null),
        });
      });

      if (uniqueById.length) {
        // The API may return depots[] as leg records (one row per leg, not per stop):
        // each row carries the departure from that depot and the arrival at the NEXT depot.
        // Detection: the origin row has both a departure and a (different) arrival, which is
        // impossible for a stop. In that case rebuild stop-level dates from the leg rows.
        const firstRow = routeDepots[0];
        const legsMode = !!(firstRow
          && firstRow.departure_date
          && firstRow.arrival_date
          && String(firstRow.arrival_date) !== String(firstRow.departure_date));

        if (legsMode) {
          const legByDepot = new Map();
          routeDepots.forEach(row => {
            const rowId = Number(row.depot_id !== undefined && row.depot_id !== null ? row.depot_id : row.id);
            if (!Number.isNaN(rowId)) legByDepot.set(rowId, row);
          });

          uniqueById.forEach((stop, index) => {
            const isLast = index === uniqueById.length - 1;
            const leg = legByDepot.get(Number(stop.depot_id));
            const prevLeg = index > 0 ? legByDepot.get(Number(uniqueById[index - 1].depot_id)) : null;

            if (isLast) {
              // The final stop has no departing leg: arrival comes from the previous leg
              stop.departure_date = null;
              stop.hours = null;
              stop.arrival_date = (prevLeg && prevLeg.arrival_date) || stop.arrival_date || null;
            } else {
              if (leg) {
                stop.departure_date = leg.departure_date || stop.departure_date || null;
                stop.hours = leg.hours != null ? leg.hours : stop.hours || null;
              }
              if (index === 0) {
                // The origin never "arrives" — the row's arrival belongs to the next depot
                stop.arrival_date = null;
              } else if (prevLeg && prevLeg.arrival_date) {
                stop.arrival_date = prevLeg.arrival_date;
              }
            }
          });
        }

        const routeDeparture = route.departure || route._departure_date || null;
        const routeArrival = route.arrival || route._arrival_date || null;
        if (routeDeparture && !uniqueById[0].departure_date) {
          uniqueById[0].departure_date = routeDeparture;
        }
        if (routeArrival && !uniqueById[uniqueById.length - 1].arrival_date) {
          uniqueById[uniqueById.length - 1].arrival_date = routeArrival;
        }
      }

      return uniqueById;
    },
    hopColor(index) {
      if (index === 0) return 'green';
      if (index === this.selectedOptionDepots.length - 1) return 'red darken-1';
      return 'orange darken-1';
    },
    hopColorHex(index) {
      if (index === 0) return '#4CAF50';
      if (index === this.selectedOptionDepots.length - 1) return '#E53935';
      return '#FB8C00';
    },
    hopLabel(index) {
      if (index === 0) return 'Origin';
      if (index === this.selectedOptionDepots.length - 1) return 'Destination';
      return 'Transit';
    },
    onDeliveryDateChange(date) {
      this.deliveryDate = date;
    },
    onEarliestConsolidationChange(date) {
      this.earliestConsolidation = date;
    },
    allowedDeliveryDates(val) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + 14);
      const d = new Date(val + 'T00:00:00');
      return d >= today && d <= maxDate;
    },
    allowedEarliestConsolidationDates(val) {
      const cutoffs = this.cutoffs;
      if (!cutoffs) return true;

      // Data-driven: enable exactly the dates the API offers
      // (today through the day before the last feasible linehaul departure)
      if (Array.isArray(cutoffs.earliest_consolidation_dates)) {
        return cutoffs.earliest_consolidation_dates.includes(val);
      }

      // Fallback for older API responses: derive bounds from the cutoffs
      const d = new Date(val + 'T00:00:00');

      let minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
      [cutoffs.submission_cutoff, cutoffs.modify_cutoff].forEach(cutoff => {
        if (!cutoff) return;
        const cutoffDate = new Date(String(cutoff).slice(0, 10) + 'T00:00:00');
        if (cutoffDate > minDate) minDate = cutoffDate;
      });
      if (d < minDate) return false;

      if (cutoffs.latest_consolidation) {
        const maxDate = new Date(String(cutoffs.latest_consolidation).slice(0, 10) + 'T00:00:00');
        if (d > maxDate) return false;
      }

      return true;
    },
    onSuburbSelect(displayVal) {
      if (!displayVal) {
        this.selectedSuburbData = null;
        return;
      }
      this.selectedSuburbData = this.suburbSuggestions.find(s => s.display === displayVal) || null;
    },
    async searchSuburbs(query) {
      this.suburbLoading = true;
      try {
        const response = await axios.get(process.env.API_URL + '/zones/autocomplete?s=' + encodeURIComponent(query));
        if (response.data && Array.isArray(response.data)) {
          this.suburbSuggestions = response.data.map(item => {
            const parsed = {};
            if (item.suburb) {
              const parts = item.suburb.split(',').map(p => p.trim());
              parsed.suburb_name = parts[0] || null;
              parsed.postcode = parts[1] || null;
            }
            return {
              ...item,
              ...parsed,
              display: item.suburb || '',
            };
          });
        }
      } catch (error) {
        this.suburbSuggestions = [];
      } finally {
        this.suburbLoading = false;
      }
    },
    getDepotName(depotId) {
      const depot = this.allDepots.find(d => d.id === depotId);
      return depot ? depot.name : null;
    },
    formatDateTime(value) {
      if (value === null || value === undefined) return value;
      // Strip seconds from "YYYY-MM-DD HH:MM:SS" datetimes; leave dates and other formats untouched
      return String(value).replace(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}):\d{2}/, '$1');
    },
    getApiErrorMessage(error) {
      let responseData = error && error.response ? error.response.data : null;
      if (!responseData && error && error.request && error.request.responseText) {
        responseData = error.request.responseText;
      }

      if (typeof responseData === 'string' && responseData.trim()) {
        const responseText = responseData.trim();
        try {
          responseData = JSON.parse(responseText);
        } catch (parseError) {
          return responseText;
        }
      }
      if (responseData && typeof responseData === 'object') {
        if (typeof responseData.error === 'string' && responseData.error.trim()) return responseData.error.trim();
        if (typeof responseData.message === 'string' && responseData.message.trim()) return responseData.message.trim();
        if (Array.isArray(responseData.errors) && responseData.errors.length) {
          return responseData.errors.map(item => typeof item === 'string' ? item : item.message).filter(Boolean).join(', ');
        }
      }

      return (error && error.message) || 'Failed to fetch linehaul routes';
    },
    showError(message) {
      this.errorMessage = message;
      this.errorDialog = true;
    },
    scrollToSection(refName) {
      this.$nextTick(() => {
        const el = this.$refs[refName];
        const node = Array.isArray(el) ? el[0] : el;
        if (!node) return;
        const target = node.$el || node;
        if (target && typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    async fetchClients() {
      this.clientsLoading = true;
      try {
        const response = await axios.get(process.env.API_URL + '/clients');
        if (response.status === 200) {
          this.clients = response.data.sort((a, b) => (a.name > b.name) ? 1 : -1);
        }
      } catch (error) {
        this.$store.commit('displaySnackbar', 'Failed to load clients');
      } finally {
        this.clientsLoading = false;
      }
    },
    async fetchDepots() {
      try {
        const response = await axios.get(process.env.API_URL + '/depots');
        if (response.status === 200) {
          this.allDepots = response.data;
        }
      } catch (error) {
        this.$store.commit('displaySnackbar', 'Failed to load depots');
      }
    },
    async goToCutoffs() {
      if (!this.canGoNext) return;

      this.cutoffsLoading = true;
      this.hasSearched = false;
      this.routesResult = null;
      this.routeOptions = [];
      this.selectedOption = null;

      try {
        const params = {
          suburb: this.selectedSuburbData.suburb_name,
          postcode: this.selectedSuburbData.postcode,
          delivery_date: this.deliveryDate,
        };
        if (this.isInternalUser) {
          params.client_id = this.selectedClientId;
        }
        const response = await axios.get(process.env.API_URL + '/linehauls/cutoffs', { params });

        if (response.status === 200 && response.data) {
          this.cutoffs = response.data;
          this.selectedConsolidationDepot = null;
          this.showAdvancedOptions = false;
          if (this.isPickPack) {
            // Pre-select the first available production date (user can change or clear it)
            this.earliestConsolidation = (Array.isArray(response.data.productions) && response.data.productions.length)
              ? response.data.productions[0].date
              : null;
          } else {
            // Pre-select the latest offered date (tightest lifecycle); empty = no feasible linehaul
            const dates = Array.isArray(response.data.earliest_consolidation_dates) ? response.data.earliest_consolidation_dates : [];
            this.earliestConsolidation = dates.length ? dates[dates.length - 1] : null;
          }

          // Diagnostics: surface data-quality signals from the cutoffs response
          if (response.data.delivery_depot_id === null || response.data.delivery_depot_id === undefined) {
            console.warn('[LinehaulCalculator] /linehauls/cutoffs returned no delivery_depot_id — suburb/postcode did not resolve to a depot (data issue, not a linehaul issue). Response:', response.data);
          }
          if (!response.data.is_pick_pack && !Array.isArray(response.data.consolidation_depots)) {
            console.warn('[LinehaulCalculator] /linehauls/cutoffs returned no consolidation_depots for a last-mile client. Response:', response.data);
          }
          if (!response.data.is_pick_pack && !Array.isArray(response.data.earliest_consolidation_dates)) {
            console.warn('[LinehaulCalculator] /linehauls/cutoffs returned no earliest_consolidation_dates for a last-mile client. Response:', response.data);
          }
          if (!response.data.local && response.data.is_pick_pack && (response.data.production === null || response.data.production === undefined)) {
            console.warn('[LinehaulCalculator] /linehauls/cutoffs has local=false with no production for a pick pack client — likely a backend/productions issue (check API logs). Response:', response.data);
          }
          if (!response.data.local && !response.data.is_pick_pack && (response.data.recommended_depot === null || response.data.recommended_depot === undefined)) {
            console.warn('[LinehaulCalculator] /linehauls/cutoffs has local=false with no recommended_depot — likely a backend/productions issue (check API logs). Response:', response.data);
          }

          this.scrollToSection('cutoffsArea');
        } else {
          this.showError('Failed to load cutoffs for the selected parameters');
        }
      } catch (error) {
        console.error('Linehaul cutoffs error:', error);
        this.showError(this.getApiErrorMessage(error));
      } finally {
        this.cutoffsLoading = false;
      }
    },
    async calculateRoutes() {
      if (!this.canCalculate || !this.selectedSuburbData) return;

      this.routesLoading = true;
      this.hasSearched = true;
      this.selectedOption = null;
      this.routeOptions = [];
      this.routesResult = null;

      try {
        const params = {
          suburb: this.selectedSuburbData.suburb_name,
          postcode: this.selectedSuburbData.postcode,
          delivery_date: this.deliveryDate,
        };
        if (this.isPickPack) {
          // Only send the fine-tuned production date while it is still offered by /cutoffs - a
          // stale selection (e.g. kept from a previous search) would be rejected by /routes with
          // "No productions available". When productions is absent (older API) send it as before.
          const offeredProductions = this.cutoffs && Array.isArray(this.cutoffs.productions) ? this.cutoffs.productions.map(p => p.date) : null;
          if (this.earliestConsolidation && (!offeredProductions || offeredProductions.includes(this.earliestConsolidation))) {
            params.earliest_consolidation = this.earliestConsolidation;
          }
        } else {
          // Only send consolidation_depot when the user explicitly overrides;
          // omitting it makes the API use the recommended depot
          if (this.selectedConsolidationDepot) {
            params.consolidation_depot = this.selectedConsolidationDepot;
          }
          if (this.earliestConsolidation) {
            params.earliest_consolidation = this.earliestConsolidation;
          }
        }
        if (this.isInternalUser) {
          params.client_id = this.selectedClientId;
        }
        const response = await axios.get(process.env.API_URL + '/linehauls/routes', { params });

        if (response.status === 200 && response.data) {
          const data = response.data;
          const routes = Array.isArray(data) ? data : (data.routes || []);

          this.routesResult = {
            local: !!data.local,
            message: data.message || null,
            production: data.production || null,
            routes,
          };

          this.routeOptions = routes.map(route => {
            const r = { ...route };
            r.depots = this.normalizeRouteDepots(r);
            r._departure_date = this.getDepartureDate(r);
            r._arrival_date = this.getArrivalDate(r);
            r.total_legs = route.total_legs != null ? route.total_legs : (r.depots && r.depots.length ? r.depots.length - 1 : null);
            r.consolidation_cutoff = route.consolidation_cutoff || route.consolidationCutoff || null;
            r.total_lifecycle_hours = route.total_lifecycle_hours != null ? route.total_lifecycle_hours : (route.totalLifecycleHours != null ? route.totalLifecycleHours : null);
            return r;
          });

          this.scrollToSection('resultsArea');
        } else {
          this.routesResult = { local: false, message: null, production: null, routes: [] };
        }
      } catch (error) {
        console.error('Linehaul calculator error:', error);
        this.routesResult = { local: false, message: null, production: null, routes: [] };
        this.showError(this.getApiErrorMessage(error));
      } finally {
        this.routesLoading = false;
      }
    },
    onRouteMapReady() {
      if (this.selectedOption) this.updateRouteMap();
    },
    tooltipDirection(index) {
      const directions = ['top', 'bottom', 'right', 'left'];
      return directions[index % directions.length];
    },
    tooltipOffset(index) {
      const offsets = [[0, -14], [0, 14], [14, 0], [-14, 0]];
      return offsets[index % offsets.length];
    },
    createDepotIcon(index, total) {
      const color = index === 0 ? '#4CAF50' : (index === total - 1 ? '#E53935' : '#FB8C00');
      const size = 16;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 1}" fill="${color}" stroke="#fff" stroke-width="2"/>
      </svg>`;
      return L.divIcon({
        className: '',
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    createLegIcon(legNumber, color) {
      const size = 28;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="${color}"/>
        <text x="${size/2}" y="${size/2 + 1}" text-anchor="middle" dominant-baseline="central" fill="#fff" font-size="12" font-weight="bold" font-family="Arial">${legNumber}</text>
      </svg>`;
      return L.divIcon({
        className: '',
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    createArrowIcon(bearing, color) {
      const size = 22;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <polygon points="11,2 19,19 11,14 3,19" fill="${color}" transform="rotate(${bearing}, 11, 11)"/>
      </svg>`;
      return L.divIcon({
        className: '',
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    getBearing(from, to) {
      const dLng = (to[1] - from[1]) * Math.PI / 180;
      const lat1 = from[0] * Math.PI / 180;
      const lat2 = to[0] * Math.PI / 180;
      const y = Math.sin(dLng) * Math.cos(lat2);
      const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
      return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
    },
    getDepotCoords(depotId, depotOverride = null) {
      const cacheKey = `${depotId}:${depotOverride ? 'override' : 'allDepots'}`;
      if (this.depotCoordsCache[cacheKey]) return this.depotCoordsCache[cacheKey];

      const depot = depotOverride || this.allDepots.find(d => d.id == depotId);
      if (!depot) return null;

      const rawLat = depot.latitude !== undefined && depot.latitude !== null ? depot.latitude : (depot.lat !== undefined ? depot.lat : null);
      const rawLng = depot.longitude !== undefined && depot.longitude !== null ? depot.longitude : (depot.lng !== undefined ? depot.lng : null);
      const lat = parseFloat(rawLat);
      const lng = parseFloat(rawLng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        const masterDepot = this.allDepots.find(d => d.id == depotId);
        if (!masterDepot) return null;
        const masterLat = parseFloat(masterDepot.latitude);
        const masterLng = parseFloat(masterDepot.longitude);
        if (!Number.isFinite(masterLat) || !Number.isFinite(masterLng)) return null;
        const coords = { lat: masterLat, lng: masterLng };
        this.depotCoordsCache[cacheKey] = coords;
        return coords;
      }

      const coords = { lat, lng };
      this.depotCoordsCache[cacheKey] = coords;
      return coords;
    },
    async fetchRoadRoute(from, to) {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
          return data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
        }
      } catch (e) {
        console.warn('OSRM routing failed, falling back to straight line', e);
      }
      return [from, to];
    },
    pointAlongPath(path, fraction) {
      if (path.length < 2) return path[0];
      const lengths = [];
      let total = 0;
      for (let i = 1; i < path.length; i++) {
        const dx = path[i][0] - path[i - 1][0];
        const dy = path[i][1] - path[i - 1][1];
        const seg = Math.sqrt(dx * dx + dy * dy);
        lengths.push(seg);
        total += seg;
      }
      let target = total * fraction;
      for (let i = 0; i < lengths.length; i++) {
        if (target <= lengths[i]) {
          const t = lengths[i] > 0 ? target / lengths[i] : 0;
          return [
            path[i][0] + (path[i + 1][0] - path[i][0]) * t,
            path[i][1] + (path[i + 1][1] - path[i][1]) * t,
          ];
        }
        target -= lengths[i];
      }
      return path[path.length - 1];
    },
    async updateRouteMap() {
      this.routeMapMarkers = [];
      this.routeMapLegs = [];
      this.mapLoading = false;
      this.initialBounds = null;

      if (!this.selectedOption || !this.selectedOptionDepots.length) return;

      this.mapLoading = true;

      const depots = this.selectedOptionDepots;
      const coords = [];
      for (let i = 0; i < depots.length; i++) {
        const d = depots[i];
        const c = this.getDepotCoords(d.depot_id, d);
        if (c) {
          const name = d.name || this.getDepotName(d.depot_id) || ('Depot ' + d.depot_id);
          const isFirst = i === 0;
          const isLast = i === depots.length - 1;
          const fallbackDeparture = isFirst && this.selectedOption && this.selectedOption.departure ? this.selectedOption.departure : null;
          const fallbackArrival = isLast && this.selectedOption && this.selectedOption.arrival ? this.selectedOption.arrival : null;
          const dateLabel = isLast
            ? (d.arrival_date || fallbackArrival ? '(arr: ' + (d.arrival_date || fallbackArrival) + ')' : '')
            : (d.departure_date || fallbackDeparture ? '(dep: ' + (d.departure_date || fallbackDeparture) + ')' : '');
          coords.push({ latLng: [c.lat, c.lng], name, dateLabel });
        }
      }

      this.routeMapMarkers = coords;

      const legColors = ['#1565C0', '#E53935', '#2E7D32', '#F57C00', '#6A1B9A', '#00838F'];
      const legs = [];
      for (let i = 0; i < coords.length - 1; i++) {
        const from = coords[i].latLng;
        const to = coords[i + 1].latLng;
        const path = await this.fetchRoadRoute(from, to);
        const midpoint = this.pointAlongPath(path, 0.5);
        const arrowPoint = this.pointAlongPath(path, 0.75);
        legs.push({
          path,
          color: legColors[i % legColors.length],
          midpoint,
          arrowPoint,
          bearing: this.getBearing(from, to),
        });
      }
      this.routeMapLegs = legs;

      await this.$nextTick();
      const map = this.$refs.routeMap ? this.$refs.routeMap.mapObject : null;
      if (map && coords.length >= 2) {
        const bounds = L.latLngBounds(coords.map(c => c.latLng));
        this.initialBounds = bounds;
        map.fitBounds(bounds, { padding: [40, 40] });
      }
      this.mapLoading = false;
    },
    resetMap() {
      const map = this.$refs.routeMap ? this.$refs.routeMap.mapObject : null;
      if (map && this.initialBounds) {
        map.fitBounds(this.initialBounds, { padding: [40, 40] });
      }
    },
    toggleDetails(item) {
      if (this.selectedOption === item) {
        this.selectedOption = null;
      } else {
        this.selectedOption = item;
      }
    },
  }
};
</script>

<style scoped>
.linehaul-calculator {
  background: #f5f6f8;
  min-height: 100vh;
}

/* ── Header ── */
.page-header {
  background: #fff;
  border-bottom: 1px solid #e2e5ea;
}

.header-icon-badge {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: transparent;
  border: 1.5px solid #1976d2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon-badge__icon {
  color: #1976d2;
}

.header-tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid #1976d2;
  color: #1976d2;
  background: transparent;
}

.header-date-chip {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 4px;
  border: 1px solid #d2d6dc;
  color: #4a5568;
  background: transparent;
}

.header-date-chip .v-icon {
  color: #9ca3af !important;
  margin-right: 2px;
}

/* ── Form Card ── */
.form-card {
  position: relative;
  overflow: hidden;
  border: 1px solid #e2e5ea;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.form-card-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #1976d2, transparent 60%);
}

/* ── Step Chip ── */
.step-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 12px;
  background: #e8f0fe;
  color: #1565c0;
}

/* ── Stepper Bar ── */
.stepper-bar {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
  padding: 12px 20px;
  overflow-x: auto;
}

.stepper-item {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.stepper-dot {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  border: 2px solid #d2d6dc;
  color: #9ca3af;
  background: #fff;
  transition: all 0.2s ease;
}

.stepper-label {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  margin-left: 8px;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.stepper-item--active .stepper-dot {
  border-color: #1976d2;
  color: #1976d2;
  background: #e8f0fe;
}

.stepper-item--active .stepper-label {
  color: #1565c0;
}

.stepper-item--done .stepper-dot {
  border-color: #2e7d32;
  color: #2e7d32;
  background: #e8f5e9;
}

.stepper-item--done .stepper-label {
  color: #2e7d32;
}

.stepper-line {
  flex: 1 1 auto;
  min-width: 24px;
  height: 2px;
  background: #e2e5ea;
  margin: 0 12px;
  border-radius: 1px;
}

.stepper-line--filled {
  background: #2e7d32;
}

/* ── Field Labels ── */
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #5f6b7a;
  margin-bottom: 4px;
}

/* ── Enterprise Inputs ── */
::v-deep .enterprise-inputs .v-input--is-outlined .v-input__slot {
  background: #fff !important;
  border-radius: 6px !important;
  min-height: 38px !important;
}

::v-deep .enterprise-inputs .v-input--is-outlined fieldset {
  border-color: #d2d6dc !important;
  border-width: 1px !important;
  transition: border-color 0.15s ease;
}

::v-deep .enterprise-inputs .v-input--is-focused fieldset {
  border-color: #1976d2 !important;
  border-width: 1.5px !important;
}

::v-deep .enterprise-inputs .v-input--is-outlined:hover fieldset {
  border-color: #9ca3af !important;
}

::v-deep .enterprise-inputs .v-input__prepend-inner .v-icon {
  font-size: 13px !important;
  color: #9ca3af !important;
}

::v-deep .enterprise-inputs .v-input--is-focused .v-input__prepend-inner .v-icon {
  color: #1976d2 !important;
}

::v-deep .enterprise-inputs .v-text-field input,
::v-deep .enterprise-inputs .v-select__selection {
  font-size: 14px !important;
  color: #2d3748 !important;
}

::v-deep .enterprise-inputs .v-text-field input::placeholder {
  color: #a0aec0 !important;
  font-size: 14px !important;
}

::v-deep .enterprise-inputs .v-label {
  display: none !important;
}

.search-btn {
  border-radius: 6px !important;
  letter-spacing: 0.2px;
  font-size: 13px !important;
  box-shadow: none !important;
  transition: all 0.15s ease;
  border-width: 1.5px !important;
}

.search-btn:hover:not(:disabled) {
  background: rgba(25, 118, 210, 0.04) !important;
}

/* ── Cutoffs meta chips ── */
.cutoffs-meta {
  margin-top: 4px;
}

.note-alert {
  border-left: 3px solid #f9a825 !important;
}

.note-text {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #4e342e;
  line-height: 1.55;
}

.note-timezone {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 500;
  color: #8d6e63;
}

.alert-text {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.55;
}

/* ── Local Banner ── */
.local-banner {
  border: 1px solid #e2e5ea;
  border-left: 3px solid #2e7d32;
  background: #f1f8f2 !important;
}

/* ── Production Panel ── */
.production-panel {
  border: 1px solid #e2e5ea;
  border-top: 2px solid #2e7d32;
}

/* ── Summary Banner ── */
.summary-banner {
  border: 1px solid #e2e5ea;
  border-left: 3px solid #2e7d32;
  background: #fff;
}

.summary-badge {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid #2e7d32;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-badge .v-icon {
  color: #2e7d32 !important;
}

/* ── Table ── */
.table-card {
  border: 1px solid #e2e5ea;
  overflow: hidden;
}

::v-deep .routes-table .v-data-table-header {
  background: #f8f9fa;
}

::v-deep .routes-table .v-data-table-header th {
  font-size: 12px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #5f6b7a !important;
  border-bottom: 1px solid #e2e5ea !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  white-space: nowrap;
}

::v-deep .routes-table tbody td {
  font-size: 14px !important;
  padding-top: 11px !important;
  padding-bottom: 11px !important;
  color: #2d3748;
  font-variant-numeric: tabular-nums;
}

::v-deep .routes-table tbody tr {
  cursor: pointer;
  transition: background-color 0.1s ease;
}

::v-deep .routes-table tbody tr:hover {
  background-color: #f0f4f8 !important;
}

.table-row.selected-row {
  background-color: #e8f0fe !important;
  box-shadow: inset 3px 0 0 #1565c0;
}

.table-row.selected-row:hover {
  background-color: #d4e4fc !important;
}

.legs-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  border-radius: 4px;
  background: transparent;
  border: 1px solid #cbd5e0;
  font-size: 13px;
  font-weight: 700;
  color: #4a5568;
  padding: 0 6px;
}

.expand-icon {
  transition: transform 0.15s ease;
}

.table-footnote {
  border-top: 1px solid #e2e5ea;
  background: #fbfcfd;
}

/* ── Detail Panel ── */
.detail-card {
  border: 1px solid #e2e5ea;
  border-top: 2px solid #1976d2;
  overflow: hidden;
}

.detail-card-header {
  background: #fff;
  border-bottom: 1px solid #e2e5ea;
}

/* ── Stat Cards ── */
.stat-card {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e2e5ea;
  transition: border-color 0.15s ease;
}

.stat-card:hover {
  border-color: #cbd5e0;
}

.stat-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
  background: transparent;
}

.stat-card--blue .stat-card__icon { border: 1.5px solid #1565c0; }
.stat-card--blue .stat-card__icon .v-icon { color: #1565c0 !important; }
.stat-card--teal .stat-card__icon { border: 1.5px solid #00796b; }
.stat-card--teal .stat-card__icon .v-icon { color: #00796b !important; }
.stat-card--indigo .stat-card__icon { border: 1.5px solid #37474f; }
.stat-card--indigo .stat-card__icon .v-icon { color: #37474f !important; }
.stat-card--green .stat-card__icon { border: 1.5px solid #2e7d32; }
.stat-card--green .stat-card__icon .v-icon { color: #2e7d32 !important; }
.stat-card--plain .stat-card__icon { border: 1.5px solid #5f6b7a; }
.stat-card--plain .stat-card__icon .v-icon { color: #5f6b7a !important; }

.stat-card__label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #718096;
  margin-bottom: 2px;
}

.stat-card__value {
  font-size: 16px;
  font-weight: 700;
  color: #1a202c;
  font-variant-numeric: tabular-nums;
}

/* ── Section Headers ── */
.section-header {
  display: flex;
  align-items: center;
}

/* ── Map ── */
.map-container {
  border: 1px solid #e2e5ea !important;
}

.reset-map-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2) !important;
}

.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
}

/* ── Hop Cards ── */
.hop-card {
  border: 1px solid #e2e5ea !important;
  transition: border-color 0.15s ease;
}

.hop-card:hover {
  border-color: #cbd5e0 !important;
}

.hop-date-badge {
  display: inline-flex;
  align-items: center;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 3px 8px;
}

.hop-date-text {
  font-size: 13px;
  font-weight: 700;
}

/* ── Page body ── */
.page-body {
  max-width: 1440px;
  margin: 0 auto;
}
</style>

<style>
.linehaul-calculator .v-timeline-item__dot--large {
  box-shadow: none !important;
}

.linehaul-calculator .v-timeline-item__dot--large .v-timeline-item__inner-dot {
  background: transparent !important;
}

.outline-dot-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2.5px solid currentColor;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Delivery/LinehaulCalculator.vue